/**
 * The single seam through which the web requests a mutating container action.
 *
 * Every Start / Stop / Restart / Deploy / Rollback goes through the
 * `instance-actions` Feathers service via a `create` (the service is
 * create-only — one action per POST). The web NEVER talks to Portainer/Docker
 * and NEVER sends a host key: the API resolves the host's encrypted Portainer
 * credential server-side, performs the action, and writes an append-only audit
 * row. `instanceId` is the `client_instances` PK — the API validates it against
 * the persisted map so the client cannot target an arbitrary container.
 *
 * RBAC: the caller gates rendering of the action on {@link canManageInfra}
 * (operator+); the API independently enforces operator+ on this service. This
 * helper is pure transport — it does no gating itself.
 */
import type { ServiceStoreType } from "../../types/ServiceStoreTypes";
import type {
  InstanceActionPayload,
  InstanceActionResult,
} from "./infrastructure.types";

export const INSTANCE_ACTIONS_PATH = "instance-actions";

/** Actions that recreate a container (and, bar reassign, pull an image) —
 *  minutes, not seconds. */
export const LONG_RUNNING_ACTIONS: ReadonlySet<string> = new Set([
  "deploy",
  "rollback",
  "update-to-latest",
  "reassign",
  "release",
  // An environment change recreates the container and waits for its health
  // check (up to 5 min) — the same bound as a move.
  "set-env",
]);
/**
 * Call bound for those actions (the ServiceStore default is 30 s). It must
 * outlast the longest a recreating request can take on the API — its
 * `RECREATE_WORST_CASE_MS`, 23¼ min, derived in
 * `packages/api/src/services/infrastructure/instance-actions/recreate-bounds.ts`
 * from the image pull deadline (10 min), the stop's wait for a slot in the
 * Portainer server's long-call pool (1 min), the stop's request ceiling (5 min),
 * the health wait or lost-stop settle (5 min), the restore watch (15 s) and an
 * allowance for ordinary calls (2 min). `instanceActions.test.ts` reads that
 * number and fails when this is not above it. Past this bound — a true
 * timeout — the outcome banner says HQ could not confirm what happened.
 */
export const LONG_RUNNING_ACTION_TIMEOUT_MS = 25 * 60 * 1000;

type BoundedStore = ServiceStoreType & { serviceCallTimeoutMs?: number };

/**
 * Long-action bounds currently raised, per ServiceStore, REFERENCE-COUNTED: the
 * first long action saves the store's own bound and raises it; overlapping ones
 * only count; the last to finish restores the saved value. Each call saving and
 * restoring its own "previous" let two overlapping deploys leave the store at
 * 10 minutes for good (the second saved the first's raised value and restored
 * it after the first had already put the default back).
 */
const raisedBounds = new WeakMap<object, { count: number; previous: number | undefined }>();

function raiseCallBound(store: BoundedStore): void {
  const held = raisedBounds.get(store);
  if (held) {
    held.count += 1;
    return;
  }
  raisedBounds.set(store, { count: 1, previous: store.serviceCallTimeoutMs });
  store.serviceCallTimeoutMs = LONG_RUNNING_ACTION_TIMEOUT_MS;
}

function releaseCallBound(store: BoundedStore): void {
  const held = raisedBounds.get(store);
  if (!held) return;
  held.count -= 1;
  if (held.count > 0) return;
  raisedBounds.delete(store);
  // Put back EXACTLY what was there — `undefined` (no bound) included. Skipping
  // the restore when it was undefined left the 10-minute bound on for good.
  store.serviceCallTimeoutMs = held.previous;
}

/**
 * POST one action through the `instance-actions` ServiceStore. Returns the API's
 * result (which never contains a secret). Throws on transport/validation error
 * so callers can surface the message and roll back any optimistic UI.
 *
 * `service` is the registered `instance-actions` ServiceStore
 * (`rootStore.instanceActionsStore`); the create body IS the action payload, so
 * it is passed through as an opaque payload (`as never`) — ServiceStore.create
 * is typed on the entity shape, not the action shape.
 */
export async function runInstanceAction(
  service: ServiceStoreType | null,
  payload: InstanceActionPayload
): Promise<InstanceActionResult> {
  // deploy / rollback / update-to-latest / reassign recreate the container
  // synchronously on the API (and all but reassign pull an image first), which
  // routinely takes longer than the ServiceStore's default 30 s call bound.
  // Under that bound the call reported "instance-actions: create timed out
  // after 30000ms" while the deploy went on to succeed server-side — a failure
  // message for a success. Lifecycle verbs (start/stop/restart/kill) keep the
  // default when no long action is in flight.
  const store = service as BoundedStore | null;
  const longRunning = LONG_RUNNING_ACTIONS.has(String(payload.action));
  if (store && longRunning) raiseCallBound(store);
  try {
    // throwOnError: the ServiceStore's default SWALLOWS a refused call — it
    // records the reason on `errorOnCreate` and RESOLVES undefined — so a
    // refused start / deploy / move / release came back here as "success" and
    // the error never reached the screen. Per call, so no other writer of this
    // store changes behaviour.
    return (await service?.create(payload as never, {
      throwOnError: true,
    })) as unknown as InstanceActionResult;
  } finally {
    if (store && longRunning) releaseCallBound(store);
  }
}
